import { defineMessages, type IntlShape } from 'react-intl';
import { addCtaFieldset } from '@redturtle/qubicaamf-theme/components/Blocks/extenders/cta';
import addBlocksFullWidth from '@redturtle/qubicaamf-theme/components/Blocks/extenders/addBlocksFullWidth';
import addBlockLines from '@redturtle/qubicaamf-theme/components/Blocks/extenders/addBlockLines';
import type { BlockConfigBase, Brain, JSONSchema } from '@plone/types';
import type { BannerData } from '@redturtle/qubicaamf-theme/components/Blocks/commons/Banner';

export interface VideoBannerData extends BannerData {
  '@type': 'video_banner';
  bannerVideo: Brain & { video_file: string };
}

const VideoBannerSchema = ({
  data,
  intl,
}: {
  data: VideoBannerData;
  intl: IntlShape;
}): JSONSchema => {
  const schema = {
    title: 'VideoBanner',
    fieldsets: [
      {
        id: 'default',
        title: 'Default',
        fields: [
          'bannerVideo',
          'bannerSize',
          'bannerTitle',
          'bannerDescription',
          'showOverlay',
          'titleAsH1',
        ],
      },
    ],
    properties: {
      bannerVideo: {
        title: intl.formatMessage(messages.bannerVideo),
        widget: 'object_browser',
        mode: 'link',
        return: 'single',
        selectableTypes: ['WildcardVideo'],
      },
      bannerSize: {
        title: intl.formatMessage(messages.bannerSize),
        widget: 'size_widget',
        choices: ['Small', 'Medium', 'Large'],
      },
      bannerTitle: {
        title: intl.formatMessage(messages.bannerTitle),
      },
      bannerDescription: {
        title: intl.formatMessage(messages.bannerDescription),
        widget: 'textarea',
      },
      showOverlay: {
        title: intl.formatMessage(messages.showOverlay),
        description: intl.formatMessage(messages.showOverlayDescr),
        type: 'boolean',
      },
      titleAsH1: {
        title: intl.formatMessage(messages.bannerTitleAsH1),
        description: intl.formatMessage(messages.bannerTitleAsH1Description),
        type: 'boolean',
      },
    },
    required: ['video'],
  };

  addCtaFieldset({ schema, intl });
  addBlocksFullWidth({ schema, formData: data, intl });
  addBlockLines({ schema, formData: data, intl });
  return schema;
};

export default VideoBannerSchema;

export interface VideoBannerConfig
  extends Omit<BlockConfigBase, 'blockSchema'> {
  blockSchema: typeof VideoBannerSchema;
}

const messages = defineMessages({
  bannerVideo: { id: 'bannerVideo', defaultMessage: 'Banner video' },
  bannerSize: { id: 'bannerSize', defaultMessage: 'Banner size' },
  bannerTitle: { id: 'bannerTitle', defaultMessage: 'Banner title' },
  bannerDescription: {
    id: 'bannerDescription',
    defaultMessage: 'Banner description',
  },
  showOverlay: { id: 'showOverlay', defaultMessage: 'Show overlay' },
  showOverlayDescr: {
    id: 'showOverlayDescr',
    defaultMessage: 'Show dark overlay to make the banner text more visible',
  },
  bannerTitleAsH1: {
    id: 'bannerTitleAsH1',
    defaultMessage: 'Set banner title as page title',
  },
  bannerTitleAsH1Description: {
    id: 'bannerTitleAsH1Description',
    defaultMessage:
      'Set the banner title as the page title by rendering it as an <h1> html tag ',
  },
});
